import {List, Tooltip, createStyles, rem} from '@mantine/core';
import type {MantineColor, MantineSize} from '@mantine/core';
import React from 'react';

import BenchlingIcon, {type BenchlingIconName} from 'ui-library/icons/BenchlingIcon';

import StatusPill from './StatusPill';

/**
 * Canonical union for the shared validation badge. Structurally identical to
 * `InputStatus` (new-prediction/newPredictionState), so that call site passes its
 * own type with no cast and `lib/` stays independent of feature state.
 */
export type ValidationStatus = 'empty' | 'incomplete' | 'invalid' | 'ready';

interface ValidationBadgeConfig {
  color: MantineColor;
  icon: BenchlingIconName;
  label: string;
}

const VALIDATION_BADGE_CONFIG: Record<ValidationStatus, ValidationBadgeConfig> = {
  empty: {color: 'yellow', icon: 'exclamation-triangle', label: 'Empty'},
  incomplete: {color: 'yellow', icon: 'exclamation-triangle', label: 'Incomplete'},
  invalid: {color: 'red', icon: 'circle-xmark', label: 'Invalid'},
  ready: {color: 'green', icon: 'check-circle', label: 'Ready'},
};

interface ValidationBadgeProps {
  status: ValidationStatus;
  // Every determinable reason for the status; when non-empty, the badge is wrapped in a Tooltip so
  // hovering (or focusing) it reveals why the input has this status. A single reason renders as a
  // plain line; two or more render as a bulleted list.
  reasons?: ReadonlyArray<string>;
  // Defaults to the compact `xs` size to match the design's ~11px validation pills.
  size?: MantineSize;
}

export default function ValidationBadge({
  status,
  reasons = [],
  size = 'xs',
}: ValidationBadgeProps): JSX.Element {
  const {classes} = useStyles();
  const config = VALIDATION_BADGE_CONFIG[status];

  const pill = (
    <StatusPill
      color={config.color}
      leftSection={<BenchlingIcon aria-hidden={true} icon={config.icon} />}
      size={size}
    >
      {config.label}
    </StatusPill>
  );

  if (reasons.length === 0) {
    return pill;
  }

  const label =
    reasons.length === 1 ? (
      reasons[0]
    ) : (
      <List fz="xs" spacing={2}>
        {reasons.map((reason) => (
          <List.Item key={reason}>{reason}</List.Item>
        ))}
      </List>
    );

  return (
    <Tooltip classNames={{tooltip: classes.tooltip}} label={label} multiline={true} withinPortal={true}>
      {pill}
    </Tooltip>
  );
}

const useStyles = createStyles(() => ({
  tooltip: {
    maxWidth: rem(360),
    whiteSpace: 'normal',
  },
}));
